/**
 * Phase 0.5 Component G — Break-glass request dialog.
 *
 * Creates a time-bound `break_glass` assignment. The server caps the
 * duration at 24h regardless of what's requested here. Reason is
 * mandatory and surfaces in the audit log + (eventually) a real-time
 * notification to the resource owner.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, SpecraCombobox, Textarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { PlatformUserPicker } from '../../Delivery/_shared/components/PlatformUserPicker';
import { useListBundles, useRequestBreakGlass } from './hooks';
export function BreakGlassRequestDialog({ open, onOpenChange, defaultUserId = '' }) {
    const [userId, setUserId] = useState(defaultUserId);
    const [bundleKey, setBundleKey] = useState('');
    const [scopeNodeId, setScopeNodeId] = useState('');
    const [reason, setReason] = useState('');
    const [durationHours, setDurationHours] = useState(24);
    const bundles = useListBundles(true);
    const request = useRequestBreakGlass();
    const durationValid = Number.isFinite(durationHours) && durationHours >= 1 && durationHours <= 24;
    const submit = async ()=>{
        if (!userId || !bundleKey || !scopeNodeId || !reason || !durationValid) return;
        await request.mutateAsync({
            userId,
            roleBundleKey: bundleKey,
            scopeNodeId,
            reason,
            durationHours
        });
        onOpenChange(false);
        setBundleKey('');
        setScopeNodeId('');
        setReason('');
        setDurationHours(24);
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            "data-testid": Testids.adminAccess.breakGlassDialog,
            children: [
                /*#__PURE__*/ _jsxs(DialogHeader, {
                    children: [
                        /*#__PURE__*/ _jsx(DialogTitle, {
                            children: "Break-glass access"
                        }),
                        /*#__PURE__*/ _jsxs(DialogDescription, {
                            children: [
                                "Creates a short-lived ",
                                /*#__PURE__*/ _jsx("code", {
                                    children: "break_glass"
                                }),
                                " assignment. The server caps the duration at 24 hours and writes a security-grade audit event. Use only when normal grants would be too slow."
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4 py-2",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "bg-user",
                                    children: "User"
                                }),
                                /*#__PURE__*/ _jsx(PlatformUserPicker, {
                                    id: "bg-user",
                                    testid: Testids.adminAccess.breakGlassUserSearch,
                                    triggerTestid: Testids.adminAccess.breakGlassUserInput,
                                    value: userId,
                                    onChange: setUserId,
                                    placeholder: "Search for the user receiving the break-glass grant"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "bg-bundle",
                                    children: "Role bundle"
                                }),
                                /*#__PURE__*/ _jsx(SpecraCombobox, {
                                    id: "bg-bundle",
                                    label: "Role bundle",
                                    options: (bundles.data ?? []).map((b)=>({
                                            value: b.key,
                                            label: `${b.displayName} (${b.key})`
                                        })),
                                    value: bundleKey || undefined,
                                    onValueChange: (v)=>setBundleKey(v ?? ''),
                                    placeholder: "Select a bundle",
                                    searchPlaceholder: "Search bundles…",
                                    triggerProps: {
                                        'data-testid': Testids.adminAccess.breakGlassBundleSelect
                                    }
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "bg-scope",
                                    children: "Scope node ID"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "bg-scope",
                                    "data-testid": Testids.adminAccess.breakGlassScopeInput,
                                    value: scopeNodeId,
                                    onChange: (e)=>setScopeNodeId(e.target.value),
                                    placeholder: "scope_node id"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "bg-duration",
                                    children: "Duration (hours, max 24)"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "bg-duration",
                                    "data-testid": Testids.adminAccess.breakGlassDurationInput,
                                    type: "number",
                                    min: 1,
                                    max: 24,
                                    value: durationHours,
                                    onChange: (e)=>setDurationHours(Number(e.target.value))
                                }),
                                !durationValid && /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs text-destructive",
                                    children: "Duration must be between 1 and 24 hours."
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "bg-reason",
                                    children: "Reason"
                                }),
                                /*#__PURE__*/ _jsx(Textarea, {
                                    id: "bg-reason",
                                    "data-testid": Testids.adminAccess.breakGlassReasonInput,
                                    value: reason,
                                    onChange: (e)=>setReason(e.target.value),
                                    placeholder: "Why break-glass is needed — required for audit",
                                    rows: 3
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(DialogFooter, {
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "outline",
                            onClick: ()=>onOpenChange(false),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            "data-testid": Testids.adminAccess.breakGlassSubmitButton,
                            onClick: submit,
                            disabled: request.isPending || !userId || !bundleKey || !scopeNodeId || !reason || !durationValid,
                            variant: "destructive",
                            children: request.isPending ? 'Requesting…' : 'Request break-glass'
                        })
                    ]
                })
            ]
        })
    });
}
